/** Booking record editor — one booking, its guest and its party, edited in place.
 *
 *  - `GET /bookings/edit/catalog/` — the static field registry (stage, facet, widget,
 *    guards, effects, static restech rule), the choice questions a changeset may raise and
 *    the record-changing actions the draft can queue. The same for every booking, so it is
 *    cached for an hour.
 *  - `GET /bookings/{pk}/edit_context/` — the per-booking facts: `baselines` (the `from` of
 *    every change, in the wire format), restech verdicts, party, guards, permissions.
 *  - `POST /bookings/{pk}/changeset/preview/` — plans a changeset, writes nothing. The
 *    ledger debounces it; the hook is a query keyed by the compiled body.
 *  - `POST /bookings/{pk}/changeset/` — applies it in one transaction. A 409 (field-level
 *    compare-and-set) rejects as `DriftError`, a 400 as `ChangesetValidationError`.
 *
 *  Hand-authored (console convention); mirrors `BookingRecord*` in console.openapi.yaml
 *  (contract test: bookingRecord.contract.test.ts). Plan: backend
 *  aidocs/plans/2026-10-01-console-booking-order-edit-forms.md §4.1, §5.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { RecordChangeset } from '@/features/records/editor/types';

import type {
  BookingEditCatalogEffect,
  BookingEditEffectSummary,
  CancelMode,
  UpdateEvents,
} from './bookingEdit';
import {
  bookingDetailKey,
  type BookingDetail,
  type BookingDetailTourRef,
  type BookingTenantRef,
  type NamedRef,
  type UserRef,
} from './bookings';
import { apiGet, apiPost, ConsoleApiError } from './client';
import { userContactsKey, type UserRecordContactDrift } from './userRecord';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** The spine panels in lifecycle order, then the cross-stage Communication lane. */
export const BOOKING_RECORD_STAGES = [
  'booked',
  'precheckin',
  'departure',
  'onsite',
  'after',
  'communication',
] as const;
export type BookingRecordStage = (typeof BOOKING_RECORD_STAGES)[number];

/** The tabs view's tabs (P5), in display order — the tab a field belongs to there. */
export const BOOKING_RECORD_FACETS = [
  'booking',
  'guest',
  'money',
  'checkin',
  'integration',
  'activity',
  'communication',
] as const;
export type BookingRecordFacet = (typeof BOOKING_RECORD_FACETS)[number];

/** A state condition under which a field or action is not editable on a booking. */
export type BookingRecordGuard = 'lead_only' | 'not_bespoke' | 'has_guest' | 'precheckin_enabled';

export type BookingRecordCheckinStatus =
  | 'unknown'
  | 'checked_in'
  | 'no_show'
  | 'error'
  | 'interactive'
  | 'pending'
  | 'retry_later';

/** One participant's OWN pre-check-in state (`not_started` is also what Reset sets). */
export type BookingRecordPrecheckinState = 'not_started' | 'partial' | 'complete' | 'skipped';

/** `booking` = Group_Tour_User (this booking or a sub-booking); `user` = its users.User. */
export type BookingRecordEntity = 'booking' | 'user';

export type BookingRecordFieldKind = 'editable' | 'action' | 'vendor' | 'derived';

export type BookingRecordWidget =
  | 'tour'
  | 'role'
  | 'date'
  | 'time'
  | 'datetime'
  | 'enum'
  | 'text'
  | 'textarea'
  | 'integer'
  | 'boolean'
  | 'tags'
  | 'country'
  /** The guest's contacts list — the person record's widget (UserRecordContacts). */
  | 'contacts';

/** What the importer does to a field, statically (the verdict refines it per booking). */
export type BookingRecordRestechRule =
  | 'rewritten'
  | 'when_sent'
  | 'merged'
  | 'by_mode'
  | 'conditional'
  | 'never';

export type BookingRecordVerdictCode = 'overwrites' | 'may_overwrite' | 'kept' | 'suspended';

export type BookingRecordOptionKey =
  | 'update_events'
  | 'cancel_mode'
  | 'guest_events'
  | 'custom_end'
  | 'old_tour_tags'
  | 'order_link'
  | 'user_email'
  | 'user_phone';

/** Catalog actions. `cancel` is a booking_state change on the wire, not an action. */
export type BookingRecordActionKey = 'checkin' | 'precheckin' | 'change_owner' | 'cancel';
export type BookingRecordWireAction = Exclude<BookingRecordActionKey, 'cancel'>;

export type BookingRecordErrorCode =
  | 'disabled'
  | 'invalid_request'
  | 'too_many_changes'
  | 'entity_not_allowed'
  | 'unknown_field'
  | 'unknown_action'
  | 'field_and_action'
  | 'duplicate_change'
  | 'not_editable'
  | 'invalid_value'
  | 'required_choice'
  | 'email_in_use'
  | 'unique_collision'
  | 'action_not_available'
  | 'description_too_long';

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export interface BookingRecordChoice {
  value: number | string;
  label: string;
}

export interface BookingRecordCatalogField {
  /** Unique across the catalog; the `field` of a change. */
  key: string;
  entity: BookingRecordEntity;
  /** Also editable on each party-member row (never for a `follows_lead` field). */
  party: boolean;
  label: string;
  kind: BookingRecordFieldKind;
  /** kind = action: the catalog action that changes this value. */
  action?: string | null;
  stage: BookingRecordStage;
  facet: BookingRecordFacet;
  /** The edit-mode input (kind = editable); null for the other kinds. */
  widget: BookingRecordWidget | null;
  choices: BookingRecordChoice[];
  nullable?: boolean;
  max_length?: number | null;
  min_value?: number | null;
  max_value?: number | null;
  guards: BookingRecordGuard[];
  /** Console permissions needed on top of console_bookings_edit. */
  permissions: string[];
  tenant_scoped: boolean;
  follows_lead: boolean;
  effects: string[];
  automatic: string[];
  restech_rule: BookingRecordRestechRule | null;
  restech_note?: string;
  /** vendor / derived kinds: the ConsoleBookingDetail properties the field renders. */
  detail_keys?: string[];
  /** vendor / derived kinds whose value is a per-booking fact of edit_context: its
   *  paths there (`checkin.window`, `guest.email_verified`). */
  context_keys?: string[];
  help?: string;
  warning?: string;
}

export interface BookingRecordCatalogOption {
  key: BookingRecordOptionKey;
  /** Where the answer goes in the changeset: events.update_events, options.custom_end, … */
  path: string;
  label: string;
  help?: string;
  /** `choice` — one of `values`; `contact` — the key of an enabled row of the option's
   *  contact type in the changeset's contacts value, or one of `values` (user_phone:
   *  `none`). The guest's contacts questions (user_email / user_phone) are `contact`. */
  type: 'choice' | 'contact';
  values: Array<{ value: string; label: string }>;
  /** The static default; edit_context `option_defaults` would override it per booking. */
  default: string | null;
  /** No silent default: Save stays blocked until answered. */
  required: boolean;
  /** Field / action keys whose change raises the question. */
  asked_when: string[];
  asked_when_note?: string;
}

export interface BookingRecordActionTransition {
  to: string | number;
  label: string;
  confirm?: string | null;
}

export interface BookingRecordCatalogAction {
  key: BookingRecordActionKey;
  label: string;
  /** The catalog field the action changes: checkin_status, precheckin_state, owner, booking_state. */
  field: string;
  /** `action` — a change carrying `action: <key>`; `field` — a change of `field` (cancel). */
  change: 'action' | 'field';
  scope: 'lead' | 'participant';
  transitions: BookingRecordActionTransition[];
  widget: 'user' | null;
  permissions: string[];
  guards: BookingRecordGuard[];
  effects: string[];
  automatic: string[];
  events: string[];
  guest_visible: boolean;
  one_step: boolean;
  /** False until the backend implements it — the console hides unavailable actions. */
  available: boolean;
  help?: string;
  warning?: string;
}

export interface BookingRecordCatalog {
  /** The ConsoleBookingEditEnabled kill switch (shared with the batch edit). */
  enabled: boolean;
  max_changes: number;
  description_max_length: number;
  event_tag_template: string;
  fields: BookingRecordCatalogField[];
  effects: BookingEditCatalogEffect[];
  options: BookingRecordCatalogOption[];
  actions: BookingRecordCatalogAction[];
  /** Guard code → explanation. */
  guards: Record<string, string>;
  /** restech_rule code → explanation. */
  restech_rules: Record<string, string>;
}

/** Queued action key → the baseline field it changes (`{checkin: 'checkin_status', …}`) —
 *  the DraftProvider's `actionFields`. `cancel` is a field change, so it is not listed. */
export function catalogActionFields(catalog: BookingRecordCatalog): Record<string, string> {
  return Object.fromEntries(
    catalog.actions.filter((a) => a.change === 'action').map((a) => [a.key, a.field]),
  );
}

// ---------------------------------------------------------------------------
// Edit context
// ---------------------------------------------------------------------------

export interface BookingRecordVerdict {
  verdict: BookingRecordVerdictCode;
  /** Empty when there is nothing to cite. */
  evidence: string;
}

export interface BookingRecordRestech {
  /** Integration code feeding the booking; null = not feed-managed (every verdict `kept`). */
  managed_by: string | null;
  managed_by_display?: string | null;
  account: string | null;
  source: {
    source_id?: string | null;
    origin?: string | null;
    /** The Import_Run's uuid. */
    import_run_id?: string | null;
    last_run_ts?: string | null;
  } | null;
  mode: 'full' | 'append' | null;
  permissions: { update_bookings: boolean | null; update_users: boolean | null };
  fingerprint_ts: string | null;
  fingerprint_status: 'recorded' | 'backfilled' | 'missing';
  suspended_reason: BookingRecordSuspendedReason | null;
  /** Whether …/restech_refresh/ and …/restech_raw/ can fetch this booking (a party member:
   *  its lead) from its reservation system — static; the tenant's config is checked when
   *  the action runs. */
  refresh: { supported: boolean; reason: BookingRestechUnsupportedReason | null };
  /** Field key → verdict for this booking and its guest (party members carry their own). */
  verdicts: Record<string, BookingRecordVerdict>;
}

/** Who checked the booking in, pausing its feed's webhook updates (D8). */
export type BookingRecordSuspendedReason = 'checkin_local' | 'checkin_dashboard' | 'checkin_console';

/** Why a booking cannot be fetched from its reservation system: no reservation system
 *  feeds it; its integration cannot fetch one stored booking; it carries no vendor booking
 *  id; the tenant's integration config cannot be used. */
export type BookingRestechUnsupportedReason = 'not_managed' | 'not_supported' | 'no_booking_id' | 'not_configured';

export interface BookingRecordGuest {
  /** User fields left out of `baselines["user:<user_id>"]` for this caller — the
   *  guest's contacts, date_of_birth, passport_number and address without
   *  console_user_view. Shown as hidden, never offered as an input (a change to one is a
   *  403). */
  omitted_fields: string[];
  user_id: number;
  display_name?: string | null;
  tenant_id?: number | null;
  /** How many bookings this user has — a guest change affects all of them. */
  affects_bookings: number;
  feed_managed: boolean;
  email_verified: boolean;
  email_is_fake: boolean;
}

export interface BookingRecordPartyMember {
  booking_id: number;
  user: UserRef | null;
  precheckin_state: BookingRecordPrecheckinState;
  checkin_status: BookingRecordCheckinStatus;
  verdicts: Record<string, BookingRecordVerdict>;
}

export interface BookingRecordSchedule {
  /** IANA zone every local date/time of the booking is read and written in (the tour's). */
  timezone: string;
  start_ts: string | null;
  /** YYYY-MM-DDTHH:MM in `timezone`. */
  local_start: string | null;
  /** The effective end (derived or stored, per end_ts_type). */
  end_ts: string | null;
  stored_end_ts: string | null;
  /** 0 = derived from the itinerary length; 1 = stored on the booking. */
  end_ts_type: 0 | 1;
  departure?: NamedRef | null;
}

export interface BookingRecordCheckinContext {
  window: { opens_ts: string | null; closes_ts: string | null; is_open: boolean };
  in_manifest: boolean;
  manifest_exclusion: 'booking_state' | 'staff_role' | 'party_member' | 'access_group' | null;
}

export interface BookingRecordTagCategory {
  id: number;
  code: string | null;
  name: string;
  /** The editor field owning the category's tags; null = not editable in the editor. */
  field: 'tags' | 'precheckin_tags' | null;
  multiselect?: boolean | null;
}

/** ConsoleBookingTourRef — the tour with the zone its schedule is read in (the detail's
 *  tour ref too). */
export type BookingRecordTourRef = BookingDetailTourRef;

/** `"<entity>:<id>"` → field key → current value, in the changeset wire format (the
 *  guest's `contacts` is UserRecordContacts). */
export type BookingRecordBaselines = Record<string, Record<string, unknown>>;

export interface BookingRecordEditContext {
  booking_id: number;
  generated_ts: string;
  flags: {
    booking_edit_enabled: boolean;
    /** ConsoleRestechRefreshEnabled — the header's Refresh from <restech>…. */
    restech_refresh_enabled: boolean;
  };
  /** The caller's console permissions by codename. */
  permissions: {
    console_bookings_edit: boolean;
    console_user_manage: boolean;
    console_bookings_checkin: boolean;
    [codename: string]: boolean;
  };
  tenant: BookingTenantRef;
  tour: BookingRecordTourRef | null;
  /** The booking's staff role; null for a guest booking. */
  role: NamedRef | null;
  /** Set when this booking is a party member: its lead, where follows-lead fields are edited. */
  lead: { id: number; user?: UserRef | null } | null;
  schedule: BookingRecordSchedule;
  baselines: BookingRecordBaselines;
  /** `"<entity>:<id>:<field or action>"` → the guards that fail for it. */
  blocked: Record<string, BookingRecordGuard[]>;
  guest: BookingRecordGuest | null;
  party: BookingRecordPartyMember[];
  checkin: BookingRecordCheckinContext;
  restech: BookingRecordRestech;
  tag_categories: BookingRecordTagCategory[];
  /** Per-booking overrides of the catalog option defaults (none at present). */
  option_defaults: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Changeset (request)
// ---------------------------------------------------------------------------

/** One final value per field (or queued action) with the baseline it started from.
 *  Exactly one of `field` / `action`. */
export interface BookingRecordChange {
  entity: BookingRecordEntity;
  id: number;
  field?: string;
  action?: BookingRecordWireAction;
  from: unknown;
  to: unknown;
}

/** A type, not an interface (as `BookingRecordOptions`): a booking body stays assignable
 *  to the records core's `RecordChangeset`, whose answers are keyed records. */
export type BookingRecordEvents = {
  update_events?: UpdateEvents;
  cancel_mode?: CancelMode;
  /** A guest's (or party member's user's) changes: `none` (default) emits nothing,
   *  `emit` one user.updated per changed user. */
  guest_events?: 'none' | 'emit';
};

export type BookingRecordOptions = {
  custom_end?: 'move' | 'keep';
  old_tour_tags?: 'drop' | 'keep';
  order_link?: 'move' | 'keep';
  /** The guest's contacts questions: the key of the enabled email / phone contact (of
   *  the contacts change's `to`) that becomes the user email / phone; user_phone may be
   *  `none`. */
  user_email?: string | null;
  user_phone?: string | null;
};

export interface BookingRecordChangeset {
  description?: string | null;
  events?: BookingRecordEvents;
  options?: BookingRecordOptions;
  changes: BookingRecordChange[];
}

/** The records core's changeset as this endpoint takes it. The core compiles only the
 *  catalog's fields and actions on the booking and its users, so this narrows the type,
 *  not the value — the server rejects anything else (`entity_not_allowed`). */
export function toBookingChangeset(body: RecordChangeset): BookingRecordChangeset {
  return body as BookingRecordChangeset;
}

// ---------------------------------------------------------------------------
// Preview / apply (responses)
// ---------------------------------------------------------------------------

export interface BookingRecordChangeOutcome {
  /** `"<entity>:<id>:<field or action>"`. */
  key: string;
  entity: BookingRecordEntity;
  id: number;
  field?: string | null;
  action?: string | null;
  /** `via_lead` — not sent: the server mirrors the lead's result onto a sub-booking. */
  outcome: 'change' | 'no_change' | 'via_lead';
  via_lead_of?: number | null;
  from: unknown;
  to: unknown;
  from_display?: string | null;
  to_display?: string | null;
  verdict?: BookingRecordVerdict | null;
  notes?: string[];
}

export interface BookingRecordRequiredChoice {
  option: string;
  path: string;
  required: boolean;
  /** The value the apply will use; null = unanswered (blocks Save when required). */
  answer: unknown;
  triggered_by: string[];
  reason: string;
}

export interface BookingRecordChangedBy {
  label: string;
  ts?: string | null;
  source?: string | null;
}

export interface BookingRecordConflict {
  code: 'unique_collision' | 'email_in_use' | 'drift';
  key: string;
  detail: string;
  owner_user_id?: number | null;
  /** email_in_use: the guest's contact row whose address it is. */
  contact_key?: string | null;
  /** email_in_use: the action that fixes it — the booking belongs to that guest. */
  suggested_action?: 'change_owner' | null;
  current?: unknown;
  changed_by?: BookingRecordChangedBy | null;
  /** drift of the guest's contacts: each contact that changed or went. */
  contacts?: UserRecordContactDrift[];
}

export interface BookingRecordEvent {
  code: string;
  entity: BookingRecordEntity;
  id: number;
  /** Preview: would_emit / suppressed / …; apply: emitted / not_emitted / … (EVENT_STATUS_LABELS). */
  status: string;
  guest_visible: boolean;
  /** Apply: console:booking_edit[<audit uuid>]; on a booking that took only actions, its
   *  first action's tag — console:checkin / console:precheckin (shared by every such
   *  action, so not unique to the save). */
  tag?: string | null;
  logid?: string | null;
  event_delivery_uuid?: string | null;
  detail?: string | null;
}

export interface BookingRecordPlannedStep {
  code: string;
  label: string;
  detail?: string | null;
  keys?: unknown[];
  key_labels?: string[];
}

export interface BookingRecordPreview {
  changes: BookingRecordChangeOutcome[];
  required_choices: BookingRecordRequiredChoice[];
  conflicts: BookingRecordConflict[];
  effects: Record<string, BookingEditEffectSummary>;
  events: BookingRecordEvent[];
  external: BookingRecordPlannedStep[];
  follow_ups: BookingRecordPlannedStep[];
  warnings: string[];
}

export interface BookingRecordFollowUp {
  code: string;
  kind: 'effect' | 'external' | 'procedure';
  label: string;
  status: 'done' | 'queued' | 'failed' | 'skipped';
  task_id?: string | null;
  message?: string | null;
  counts?: { ok?: number; failed?: number };
  keys?: unknown[];
}

export interface BookingRecordApplyResult {
  audit_id: number;
  audit_uuid: string;
  event_tag: string;
  saved_ts: string;
  changes: BookingRecordChangeOutcome[];
  events: BookingRecordEvent[];
  follow_ups: BookingRecordFollowUp[];
  warnings: string[];
  /** Re-seed the page from these two. */
  detail: BookingDetail;
  edit_context: BookingRecordEditContext;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export interface BookingRecordDriftedField {
  key: string;
  entity: BookingRecordEntity;
  id: number;
  field?: string | null;
  action?: string | null;
  /** The stored value now (wire format). */
  current: unknown;
  changed_by?: BookingRecordChangedBy | null;
  /** The guest's contacts: each contact that changed or went since the editor loaded it,
   *  and the user email / phone moving. */
  contacts?: UserRecordContactDrift[];
}

export interface BookingRecordFieldError {
  code: BookingRecordErrorCode;
  message: string;
  guard?: BookingRecordGuard | null;
  owner_user_id?: number | null;
  /** A contacts error: the row it concerns (UserRecordContact.key). */
  contact_key?: string | null;
  /** email_in_use: change the booking's owner to that guest instead (absent when only
   *  the write found the address — there is no owner to move the booking to). */
  suggested_action?: 'change_owner' | null;
}

/** 409 of the apply: some `from` no longer matches the stored value. Nothing was written —
 *  the client rebases the drifted entries (Keep mine / Take theirs) and resubmits. */
export class DriftError extends ConsoleApiError {
  constructor(
    status: number,
    body: ConsoleApiError['body'],
    message: string,
    public readonly drifted: BookingRecordDriftedField[],
  ) {
    super(status, body, message);
  }
}

/** 400 of the preview or apply. `errors` is keyed by where the problem is:
 *  `"<entity>:<id>:<field or action>"` for a change, `"options.<key>"` / `"events.<key>"`
 *  for a choice, `"description"`, and `"changes"` for the list as a whole. Empty for
 *  `disabled` (the kill switch). Nothing was written. */
export class ChangesetValidationError extends ConsoleApiError {
  constructor(
    status: number,
    body: ConsoleApiError['body'],
    message: string,
    public readonly code: BookingRecordErrorCode,
    public readonly errors: Record<string, BookingRecordFieldError[]>,
  ) {
    super(status, body, message);
  }

  /** The errors filed under one key (`changeKey(...)`, `"options.custom_end"`, …). */
  errorsFor(key: string): BookingRecordFieldError[] {
    return this.errors[key] ?? [];
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Re-type a changeset call's ConsoleApiError by its body; anything else passes through.
 *  A 400 counts as field-keyed only when `errors` is an object (the generic error
 *  envelope carries `errors` as a string list). */
export function toChangesetError(e: unknown): unknown {
  if (!(e instanceof ConsoleApiError)) return e;
  const body: unknown = e.body;
  if (!isPlainObject(body)) return e;
  if (e.status === 409 && body.code === 'drift' && Array.isArray(body.drifted)) {
    return new DriftError(e.status, e.body, e.message, body.drifted as BookingRecordDriftedField[]);
  }
  if (e.status === 400 && typeof body.code === 'string' && isPlainObject(body.errors)) {
    return new ChangesetValidationError(
      e.status,
      e.body,
      e.message,
      body.code as BookingRecordErrorCode,
      body.errors as Record<string, BookingRecordFieldError[]>,
    );
  }
  return e;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export function fetchBookingRecordCatalog(): Promise<BookingRecordCatalog> {
  return apiGet<BookingRecordCatalog>('/bookings/edit/catalog/');
}

export function fetchBookingEditContext(
  id: number,
  signal?: AbortSignal,
): Promise<BookingRecordEditContext> {
  return apiGet<BookingRecordEditContext>(
    `/bookings/${id}/edit_context/`,
    signal ? { signal } : undefined,
  );
}

export async function previewBookingChangeset(
  id: number,
  body: BookingRecordChangeset,
  signal?: AbortSignal,
): Promise<BookingRecordPreview> {
  try {
    return await apiPost<BookingRecordPreview>(
      `/bookings/${id}/changeset/preview/`,
      body,
      signal ? { signal } : undefined,
    );
  } catch (e) {
    throw toChangesetError(e);
  }
}

export async function applyBookingChangeset(
  id: number,
  body: BookingRecordChangeset,
): Promise<BookingRecordApplyResult> {
  try {
    return await apiPost<BookingRecordApplyResult>(`/bookings/${id}/changeset/`, body);
  } catch (e) {
    throw toChangesetError(e);
  }
}

// ---------------------------------------------------------------------------
// React Query
// ---------------------------------------------------------------------------

/** The catalog only changes with a deploy. */
export const BOOKING_RECORD_CATALOG_STALE_MS = 60 * 60_000;

export const bookingRecordKeys = {
  all: ['bookingRecord'] as const,
  catalog: () => ['bookingRecord', 'catalog'] as const,
  editContext: (id: number) => ['bookingRecord', 'editContext', id] as const,
  /** The time axis (`bookingActions.ts` fetches it; listed here so a save refreshes it). */
  timeline: (id: number) => ['bookingRecord', 'timeline', id] as const,
  preview: (id: number, body: BookingRecordChangeset) =>
    ['bookingRecord', 'preview', id, body] as const,
};

export function useBookingRecordCatalog(): UseQueryResult<BookingRecordCatalog> {
  return useQuery({
    queryKey: bookingRecordKeys.catalog(),
    queryFn: fetchBookingRecordCatalog,
    staleTime: BOOKING_RECORD_CATALOG_STALE_MS,
  });
}

export function useBookingEditContext(
  id: number,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<BookingRecordEditContext> {
  return useQuery({
    queryKey: bookingRecordKeys.editContext(id),
    queryFn: ({ signal }) => fetchBookingEditContext(id, signal),
    enabled: enabled && Number.isFinite(id),
  });
}

/** The ledger's preview of a (debounced) compiled draft; `null` = nothing to preview.
 *
 *  A POST that writes nothing, run as a query keyed by the body rather than a mutation: a
 *  newer body aborts the superseded request (no out-of-order results), the previous plan
 *  stays on screen while the next loads, and undoing back to an earlier draft is answered
 *  from the cache. A 400 surfaces as `error` (ChangesetValidationError); drift is not an
 *  error here — the preview reports it in `conflicts`. */
export function useBookingChangesetPreview(
  id: number,
  body: RecordChangeset | null,
): UseQueryResult<BookingRecordPreview> {
  const wire = body ? toBookingChangeset(body) : { changes: [] };
  const enabled = body !== null && wire.changes.length > 0 && Number.isFinite(id);
  return useQuery({
    queryKey: bookingRecordKeys.preview(id, wire),
    queryFn: ({ signal }) => previewBookingChangeset(id, wire, signal),
    enabled,
    staleTime: 0,
    gcTime: 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** Save. On success `onSaved` runs first — reset the draft there: the saved entries must
 *  be gone before the re-seeded baselines rebase it, or a value the server normalised
 *  (trimmed text) would come back as a stale entry — then the edit context and the booking
 *  detail are re-seeded from the response (no refetch). A 409 rejects with `DriftError`
 *  (the edit context is refetched for the rebase), a 400 with `ChangesetValidationError`. */
export function useApplyBookingChangeset(
  id: number,
  { onSaved }: { onSaved?: (result: BookingRecordApplyResult) => void } = {},
): UseMutationResult<BookingRecordApplyResult, unknown, RecordChangeset> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordChangeset) => applyBookingChangeset(id, toBookingChangeset(body)),
    onSuccess: (result, body) => {
      onSaved?.(result);
      qc.setQueryData(bookingRecordKeys.editContext(id), result.edit_context);
      qc.setQueryData(bookingDetailKey(id), result.detail);
      // The save is on the time axis now (its change rows, its deliveries, a replan).
      void qc.invalidateQueries({ queryKey: bookingRecordKeys.timeline(id) });
      // A contacts save: the guest's stored contacts (their dates, the Guest panel's list)
      // are new.
      const contacts = body.changes.filter((c) => c.entity === 'user' && c.field === 'contacts');
      for (const c of contacts) void qc.invalidateQueries({ queryKey: userContactsKey(c.id) });
    },
    onError: (e) => {
      // The stored values moved: fetch the fresh baselines (the draft rebases onto them).
      if (e instanceof DriftError) {
        void qc.invalidateQueries({ queryKey: bookingRecordKeys.editContext(id) });
      }
    },
  });
}
